// Wired to the real Go backend at backend (see its README), via the
// /api-auth proxy prefix (see vite.config.ts). Only /login and /register
// exist server-side today (single-token model, no separate refresh secret)
// - every other domain (channels, templates, workflows, stats...) has no
// backend yet and is served from the mock server via dataApi.ts instead.
import { readCsrfCookie, reportHttpStatus } from './dataApi';

const API_BASE = import.meta.env.VITE_AUTH_API_BASE ?? '/api-auth';

export interface AuthResult {
  success: boolean;
  token?: string;
  error?: string;
  // Set when the backend accepted the password but the account has email
  // 2FA on - no session was issued; the caller must collect the emailed
  // code and call verifyLoginCode(challengeId, code). See
  // backend/workflows/auth/login.wsl's RespondMfaRequired.
  twoFactorRequired?: boolean;
  // Set when the account has an authenticator app enrolled instead - the
  // caller must collect a code from that app (or a backup code) and call
  // verifyAuthenticatorCode(challengeId, code). See login.wsl's
  // RespondAuthenticatorRequired.
  authenticatorRequired?: boolean;
  challengeId?: string;
}

function extractToken(body: any): string | undefined {
  const data = body?.data ?? body;
  if (typeof data === 'string') return data;
  return data?.accessToken ?? data?.token ?? data?.access_token ?? data?.jwt ?? undefined;
}

function extractError(body: any): string | undefined {
  return (
    body?.data?.error ??
    body?.error ??
    body?.meta?.error?.message ??
    (typeof body?.data === 'string' && body?.success === false ? body.data : undefined)
  );
}

// The backend can't produce clean client-facing error messages today (its
// own README documents why: a Debug-config bug means every wrapped
// transition error comes through as a raw " trace: ..." dump instead of the
// short message the workflow actually intended). Recognize the two dumps
// login/register can produce and translate them; anything else unrecognized
// still gets shown rather than swallowed, just without the trace noise.
function cleanAuthError(raw: string): string {
  if (/redis: nil/.test(raw) && /LookupUser|passwordHash/.test(raw)) {
    return 'Invalid email or password.';
  }
  if (/ComparePassword/.test(raw)) {
    return 'Invalid email or password.';
  }
  if (/\btrace:\s*workflows\//.test(raw)) {
    return 'Something went wrong on the server. Please try again.';
  }
  return raw;
}

// credentials: 'include' is required here (not just on fetchSecurity/
// logout below) for the session cookie this response's Set-Cookie header
// carries to actually get stored at all - fetch()'s default credentials
// mode is "same-origin", which silently DROPS Set-Cookie for a cross-
// origin response instead of erroring, so this bug was invisible in local
// dev (API_BASE resolves to a same-origin path riding the Vite proxy -
// see .env.example's VITE_AUTH_API_BASE comment) and only broke in a real
// deployment (API_BASE is an absolute cross-origin URL there). The
// symptom was exactly "login looks like it works" (this call's own
// response body still carries the token, which login() reads directly to
// set isLoggedIn optimistically) "but a page reload logs you out again"
// (the boot-time fetchSecurity() check has no cookie to send, since one
// was never actually stored).
async function postAuth(path: string, payload: Record<string, unknown>): Promise<AuthResult> {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    let body: any = null;
    try {
      body = await response.json();
    } catch {
      // no JSON body
    }
    const data = body?.data ?? body;
    if (response.ok && data && typeof data === 'object' && data.twoFactorRequired && typeof data.challengeId === 'string') {
      return { success: true, twoFactorRequired: true, challengeId: data.challengeId };
    }
    if (response.ok && data && typeof data === 'object' && data.authenticatorRequired && typeof data.challengeId === 'string') {
      return { success: true, authenticatorRequired: true, challengeId: data.challengeId };
    }
    const token = extractToken(body);
    if (response.ok && token) {
      return { success: true, token };
    }
    const rawError = extractError(body) ?? `Request failed (${response.status})`;
    return { success: false, error: cleanAuthError(rawError) };
  } catch (err) {
    return { success: false, error: 'Could not reach the server. Is the backend running?' };
  }
}

export const authenticateUser = (email: string, password: string) => postAuth('/login', { email, password });

// Second step of an email-2FA login: exchanges the challengeId from
// authenticateUser + the emailed code for a real session (the verify
// response carries the same Set-Cookie a normal /login does, stored
// because postAuth sends credentials: 'include'). Backed by POST
// /login/verify (backend/workflows/auth/login_verify.wsl).
export const verifyLoginCode = (challengeId: string, code: string) =>
  postAuth('/login/verify', { challengeId, code });

// Second step of an authenticator-app login: exchanges the challengeId
// from authenticateUser + a code from the user's app (or one of their
// backup codes) for a real session. Backed by POST
// /login/verify-authenticator (backend/workflows/auth/login_verify_authenticator.wsl).
export const verifyAuthenticatorCode = (challengeId: string, code: string) =>
  postAuth('/login/verify-authenticator', { challengeId, code });

// Re-sends the 2FA code for a still-pending challenge. Backed by POST
// /login/resend - always 200 with a generic message, so this resolves to
// a string rather than throwing on "challenge not found".
export async function resendLoginCode(challengeId: string): Promise<string> {
  const result = await postMessage('/login/resend', { challengeId });
  return result.message ?? 'If that request is still valid, a new code was sent.';
}

// register.wsl requires name and nickname in the body (400s without them),
// plus - since roadmap item #2 - the challengeId + code from
// sendRegisterCode(): no unverified account is ever created.
export const registerUser = (
  name: string,
  nickname: string,
  email: string,
  password: string,
  challengeId: string,
  code: string,
) => postAuth('/register', { name, surname: '', fullname: name, nickname, email, password, challengeId, code });

// Step 1 of signup: emails a one-time verification code for `email` and
// returns the challengeId to submit back with registerUser(). Backed by
// POST /register/send-code - throws on the disposable-email 400, and (once
// the backend's TURNSTILE_SECRET_KEY is configured - see .env.example) the
// bot-check 400 too. turnstileToken is the Cloudflare Turnstile widget's
// token (TurnstileWidget); "" when VITE_TURNSTILE_SITE_KEY is unset, which
// the backend's VerifyTurnstile treats as a no-op the same way.
export async function sendRegisterCode(email: string, turnstileToken: string): Promise<string> {
  const result = await postMessage('/register/send-code', { email, turnstileToken });
  if (!result.success || !result.challengeId) {
    throw new Error(result.error ?? 'Could not send a verification code.');
  }
  return result.challengeId;
}

// Re-sends the signup verification code. Backed by POST
// /register/resend-code - always 200 with a generic message.
export async function resendRegisterCode(challengeId: string): Promise<string> {
  const result = await postMessage('/register/resend-code', { challengeId });
  return result.message ?? 'If that request is still valid, a new code was sent.';
}

interface MessageResult {
  success: boolean;
  message?: string;
  error?: string;
  challengeId?: string;
}

// /password/forgot and /password/reset respond with {message: "..."} rather
// than a token, so this doesn't reuse postAuth's token-shaped success check.
async function postMessage(path: string, payload: Record<string, unknown>): Promise<MessageResult> {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    let body: any = null;
    try {
      body = await response.json();
    } catch {
      // no JSON body
    }
    const data = body?.data ?? body;
    const challengeId =
      data && typeof data === 'object' && typeof data.challengeId === 'string' ? data.challengeId : undefined;
    // Some of these endpoints return {challengeId} with no message
    // (/register/send-code), some {message, challengeId} (/password/forgot),
    // some just {message} (/password/reset, resends).
    if (response.ok && data && typeof data === 'object' && !data.error && (challengeId || typeof data.message === 'string')) {
      return { success: true, message: data.message, challengeId };
    }
    const rawError = extractError(body) ?? `Request failed (${response.status})`;
    return { success: false, error: cleanAuthError(rawError) };
  } catch (err) {
    return { success: false, error: 'Could not reach the server. Is the backend running?' };
  }
}

export interface PasswordResetRequest {
  message: string;
  challengeId: string;
}

// Backed by POST /password/forgot - emails a one-time reset code and
// returns {message, challengeId} (roadmap item #2: was an emailed link).
// The message deliberately doesn't reveal whether the account exists.
export async function sendPasswordReset(email: string): Promise<PasswordResetRequest> {
  const result = await postMessage('/password/forgot', { email });
  if (!result.success || !result.challengeId) {
    throw new Error(result.error ?? 'Something went wrong.');
  }
  return {
    message: result.message ?? 'If an account with that email exists, we sent a reset code.',
    challengeId: result.challengeId,
  };
}

// Backed by POST /password/reset - completes the flow started by
// sendPasswordReset, using the emailed code.
export async function resetPasswordWithCode(
  challengeId: string,
  code: string,
  password: string,
  confirm: string,
): Promise<string> {
  const result = await postMessage('/password/reset', { challengeId, code, password, confirm });
  if (!result.success) throw new Error(result.error ?? 'Something went wrong.');
  return result.message ?? 'Password updated.';
}

export interface SecurityCredentials {
  key: string;
  secret: string;
  // Whether email 2FA is enabled for this account (backend/workflows/user/
  // security.wsl now returns it alongside the Cue credentials).
  twofactor: boolean;
}

// Backed by GET /security (session-cookie protected - see dataApi.ts's
// header comment on credentials: 'include') - the account's real Cue
// signaling credentials, generated at registration (see
// workflows/auth/register.wsl's securityKey/securitySecret steps) and
// distinct from the login token. Without this, StoreContext's `security`
// state would stay the hardcoded local placeholder default, which the
// backend has never heard of - every Cue request under it would fail with
// "unknown security key".
export async function fetchSecurity(): Promise<SecurityCredentials | null> {
  try {
    const response = await fetch(`${API_BASE}/security`, { credentials: 'include' });
    // A 401 here after login means the session cookie expired - let
    // StoreContext's handler clear local auth state. Harmless during the
    // boot-time probe (nothing is logged in yet, so the handler no-ops).
    reportHttpStatus(response.status);
    if (!response.ok) return null;
    const body = await response.json();
    const data = body?.data;
    if (!body?.success || !data || typeof data !== 'object' || data.error) return null;
    if (typeof data.key !== 'string' || typeof data.secret !== 'string') return null;
    return { key: data.key, secret: data.secret, twofactor: data.twofactor === true };
  } catch {
    return null;
  }
}

// Enable/disable email 2FA for the signed-in caller. Backed by POST
// /2fa/enable | /2fa/disable - session-cookie + double-submit-CSRF gated
// exactly like the password-change endpoint, so this sends both
// credentials: 'include' and the X-CSRF-Token header.
export async function setTwoFactor(enabled: boolean): Promise<MessageResult> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const csrf = readCsrfCookie();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const response = await fetch(`${API_BASE}${enabled ? '/2fa/enable' : '/2fa/disable'}`, {
      method: 'POST',
      credentials: 'include',
      headers,
    });
    let body: any = null;
    try {
      body = await response.json();
    } catch {
      // no JSON body
    }
    reportHttpStatus(response.status);
    const data = body?.data ?? body;
    if (response.ok && data && typeof data === 'object' && typeof data.message === 'string') {
      return { success: true, message: data.message };
    }
    const rawError = extractError(body) ?? `Request failed (${response.status})`;
    return { success: false, error: cleanAuthError(rawError) };
  } catch {
    return { success: false, error: 'Could not reach the server. Is the backend running?' };
  }
}

// ---- authenticator app (TOTP) ---------------------------------------------
//
// Session-cookie + double-submit-CSRF gated, same shape as setTwoFactor
// above - backed by github.com/kuetix/authenticator via
// backend/workflows/auth/authenticator_*.wsl.

export interface AuthenticatorStatus {
  enabled: boolean;
  backupCodesRemaining?: number;
}

export interface AuthenticatorEnrollment {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
  expiresInSec: number;
}

interface AuthenticatorActionResult {
  success: boolean;
  ok?: boolean;
  backupCodes?: string[];
  error?: string;
}

async function authenticatedPost(path: string): Promise<{ ok: boolean; data: any; error?: string }> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const csrf = readCsrfCookie();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const response = await fetch(`${API_BASE}${path}`, { method: 'POST', credentials: 'include', headers });
    let body: any = null;
    try {
      body = await response.json();
    } catch {
      // no JSON body
    }
    reportHttpStatus(response.status);
    const data = body?.data ?? body;
    if (response.ok && data && typeof data === 'object') {
      return { ok: true, data };
    }
    return { ok: false, data: null, error: cleanAuthError(extractError(body) ?? `Request failed (${response.status})`) };
  } catch {
    return { ok: false, data: null, error: 'Could not reach the server. Is the backend running?' };
  }
}

async function authenticatedPostWithCode(path: string, code: string): Promise<{ ok: boolean; data: any; error?: string }> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const csrf = readCsrfCookie();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ code }),
    });
    let body: any = null;
    try {
      body = await response.json();
    } catch {
      // no JSON body
    }
    reportHttpStatus(response.status);
    const data = body?.data ?? body;
    if (response.ok && data && typeof data === 'object') {
      return { ok: true, data };
    }
    return { ok: false, data: null, error: cleanAuthError(extractError(body) ?? `Request failed (${response.status})`) };
  } catch {
    return { ok: false, data: null, error: 'Could not reach the server. Is the backend running?' };
  }
}

// Backed by GET /authenticator/status.
export async function fetchAuthenticatorStatus(): Promise<AuthenticatorStatus | null> {
  try {
    const response = await fetch(`${API_BASE}/authenticator/status`, { credentials: 'include' });
    reportHttpStatus(response.status);
    if (!response.ok) return null;
    const body = await response.json();
    const data = body?.data ?? body;
    if (!data || typeof data !== 'object' || typeof data.enabled !== 'boolean') return null;
    return { enabled: data.enabled, backupCodesRemaining: data.backupCodesRemaining };
  } catch {
    return null;
  }
}

// Backed by POST /authenticator/enroll/begin - mints a pending secret + QR
// code. Call confirmAuthenticatorEnroll with a code from the app next, or
// cancelAuthenticatorEnroll to abandon it.
export async function beginAuthenticatorEnroll(): Promise<AuthenticatorEnrollment> {
  const result = await authenticatedPost('/authenticator/enroll/begin');
  if (!result.ok) throw new Error(result.error ?? 'Could not start authenticator setup.');
  const { secret, otpauthUrl, qrCodeDataUrl, expiresInSec } = result.data;
  return { secret, otpauthUrl, qrCodeDataUrl, expiresInSec };
}

// Backed by POST /authenticator/enroll/confirm - on success returns 10
// one-time backup codes in plaintext, shown to the user exactly once.
export async function confirmAuthenticatorEnroll(code: string): Promise<{ ok: boolean; backupCodes?: string[]; error?: string }> {
  const result = await authenticatedPostWithCode('/authenticator/enroll/confirm', code);
  if (!result.ok) return { ok: false, error: result.error };
  const data = result.data as AuthenticatorActionResult;
  return { ok: data.ok === true, backupCodes: data.backupCodes, error: data.error };
}

// Backed by POST /authenticator/enroll/cancel.
export async function cancelAuthenticatorEnroll(): Promise<void> {
  await authenticatedPost('/authenticator/enroll/cancel');
}

// Backed by POST /authenticator/disable.
export async function disableAuthenticator(): Promise<{ ok: boolean; error?: string }> {
  const result = await authenticatedPost('/authenticator/disable');
  return { ok: result.ok, error: result.error };
}

// Backed by POST /authenticator/backup-codes/regenerate - invalidates
// existing codes and mints a fresh set.
export async function regenerateAuthenticatorBackupCodes(): Promise<{ ok: boolean; backupCodes?: string[]; error?: string }> {
  const result = await authenticatedPost('/authenticator/backup-codes/regenerate');
  if (!result.ok) return { ok: false, error: result.error };
  const data = result.data as AuthenticatorActionResult;
  return { ok: data.ok === true, backupCodes: data.backupCodes, error: data.error };
}

// Backed by POST /logout (backend/workflows/auth/logout.wsl) - clears the
// httpOnly zmist_session + zmist_csrf cookies server-side, since client
// JS can't clear an httpOnly cookie itself. Deliberately swallows any
// failure (see StoreContext.tsx's logout() - local state is cleared
// regardless of whether this network call succeeds).
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/logout`, { method: 'POST', credentials: 'include' });
  } catch {
    // best-effort - local logout already happened regardless
  }
}

// ---- social sign-in (Google/Microsoft/Apple/LinkedIn) --------------------
//
// Authorization Code flow, no client-side SDKs - see backend/modules/oauth/
// transitions/oauth.go's header comment for why. SocialLoginButtons.tsx
// fetches a ready-made authorize URL and navigates the whole page there;
// every provider redirects back to /oauth/callback/{provider}
// (OAuthCallbackScreen.tsx) with a plain ?code=&state= query string, which
// completeOAuthSignIn below exchanges for a login token exactly like
// authenticateUser/registerUser above.

export interface OAuthAuthorizeResult {
  url: string;
  state: string;
}

// Apple is the only provider that can't redirect straight back to this
// frontend's own /oauth/callback/:provider route - Apple requires
// response_mode=form_post whenever scope includes name/email (see backend's
// social-oauth/oauth.go), so code/state arrive as a POST body a client-side
// route can't read. This points Apple's redirect_uri at a small backend
// bridge (cmd/api/apple_bridge.go) that reads the POST and 302s back to the
// ordinary ?code=&state= callback instead - Google/Microsoft/LinkedIn need
// no override, they already land on the frontend route directly. The exact
// same value must be sent to both startOAuthSignIn and completeOAuthSignIn
// below, since OAuth requires the token-exchange redirect_uri to match the
// one used at the authorize step.
function oauthRedirectUriOverride(provider: string): string | null {
  return provider === 'apple' ? `${API_BASE}/oauth/callback/apple` : null;
}

export async function startOAuthSignIn(provider: string): Promise<OAuthAuthorizeResult> {
  let body: any = null;
  try {
    const redirectUri = oauthRedirectUriOverride(provider);
    const qs = new URLSearchParams({ provider });
    if (redirectUri) qs.set('redirectUri', redirectUri);
    const response = await fetch(`${API_BASE}/oauth/authorize-url?${qs.toString()}`);
    try {
      body = await response.json();
    } catch {
      // no JSON body
    }
    const data = body?.data;
    if (response.ok && data && typeof data.url === 'string' && typeof data.state === 'string') {
      return { url: data.url, state: data.state };
    }
  } catch {
    throw new Error('Could not reach the server. Is the backend running?');
  }
  const rawError = extractError(body) ?? 'Unable to start sign-in.';
  throw new Error(cleanAuthError(rawError));
}

export interface OAuthCompleteResult {
  success: boolean;
  token?: string;
  email?: string;
  error?: string;
}

// Doesn't reuse postAuth/AuthResult above - this needs `email` back too
// (unlike login/register, the frontend never typed it in; the provider is
// the one that knows it), which AuthResult doesn't carry.
export async function completeOAuthSignIn(provider: string, code: string, state: string): Promise<OAuthCompleteResult> {
  try {
    const redirectUri = oauthRedirectUriOverride(provider);
    const response = await fetch(`${API_BASE}/oauth/exchange`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(redirectUri ? { provider, code, state, redirectUri } : { provider, code, state }),
    });
    let body: any = null;
    try {
      body = await response.json();
    } catch {
      // no JSON body
    }
    const data = body?.data;
    const token = extractToken(body);
    if (response.ok && token && data && typeof data.email === 'string') {
      return { success: true, token, email: data.email };
    }
    const rawError = extractError(body) ?? `Request failed (${response.status})`;
    return { success: false, error: cleanAuthError(rawError) };
  } catch {
    return { success: false, error: 'Could not reach the server. Is the backend running?' };
  }
}
